import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getStoredToken, clearStoredToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  const loadMe = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Network / 5xx must NOT log the user out — only a real auth failure.
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearStoredToken();
        setUser(false);
      } else if (!getStoredToken()) {
        setUser(false);
      }
      // Keep existing user on transient errors so a blip does not bounce to login.
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password, captchaToken = null) => {
    const body = { email, password };
    if (captchaToken) body.captcha_token = captchaToken;
    const { data } = await api.post("/auth/login", body);
    localStorage.setItem("wa_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (payload) => {
    const body = {
      ...payload,
      // So white-label signups attach to the reseller that owns this domain.
      host: typeof window !== "undefined" ? window.location.hostname : undefined,
    };
    const { data } = await api.post("/auth/signup", body);
    // Public signup requires email verification — no session token yet.
    if (data?.requires_verification || !data?.access_token) {
      return data;
    }
    localStorage.setItem("wa_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    clearStoredToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, signup, logout, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
